import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { GitMerge, ArrowRight, Check, FileText, MessageSquare, Share2, Tag } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { statusColor } from './constants';

const MERGE_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'company', label: 'Company' },
  { key: 'address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip_code', label: 'Zip Code' },
  { key: 'status', label: 'Status' },
  { key: 'category', label: 'Category' },
  { key: 'lead_source', label: 'Source' },
];

export function MergeLeadDialog({ open, onClose, newLeadData, existingLead, onMergeComplete }) {
  const [selections, setSelections] = useState({});
  const [merging, setMerging] = useState(false);
  const [fullExisting, setFullExisting] = useState(existingLead);

  // Fetch full existing lead data when dialog opens
  useEffect(() => {
    if (open && existingLead?.id) {
      api.getLead(existingLead.id)
        .then(r => setFullExisting(r.data))
        .catch(() => setFullExisting(existingLead));
    }
  }, [open, existingLead]);

  // Initialize selections: prefer existing (primary) values by default
  useEffect(() => {
    if (!open) return;
    const initial = {};
    MERGE_FIELDS.forEach(f => {
      const existVal = fullExisting?.[f.key] || '';
      const newVal = newLeadData?.[f.key] || '';
      // Pick whichever has data; prefer existing if both have data
      let pick = 'existing';
      if (!existVal && newVal) pick = 'new';
      initial[f.key] = pick;
    });
    setSelections(initial);
  }, [open, fullExisting, newLeadData]);

  if (!existingLead || !newLeadData) return null;

  const getVal = (field, source) => {
    if (source === 'new') return newLeadData?.[field] || '';
    return fullExisting?.[field] || '';
  };

  const toggle = (key) => {
    setSelections(prev => ({ ...prev, [key]: prev[key] === 'existing' ? 'new' : 'existing' }));
  };

  const handleMerge = async () => {
    setMerging(true);
    try {
      // Build field_overrides: only send fields where user picked the "new" value
      const overrides = {};
      MERGE_FIELDS.forEach(f => {
        if (selections[f.key] === 'new' && newLeadData[f.key]) {
          overrides[f.key] = newLeadData[f.key];
        }
      });
      const { data } = await api.mergeLeads({
        primary_id: fullExisting.id,
        secondary_id: '__new__',
        field_overrides: overrides,
      });
      toast.success(data.message || 'Leads merged successfully');
      if (onMergeComplete) onMergeComplete(data.lead);
    } catch (e) {
      // If secondary doesn't exist yet (it's the form data), create it first then merge
      try {
        const payload = { ...newLeadData, tags: typeof newLeadData.tags === 'string' ? newLeadData.tags.split(',').map(t => t.trim()).filter(Boolean) : (newLeadData.tags || []) };
        const created = await api.createLead(payload);
        const overrides = {};
        MERGE_FIELDS.forEach(f => {
          if (selections[f.key] === 'new' && newLeadData[f.key]) {
            overrides[f.key] = newLeadData[f.key];
          }
        });
        const { data } = await api.mergeLeads({
          primary_id: fullExisting.id,
          secondary_id: created.data.id,
          field_overrides: overrides,
        });
        toast.success(data.message || 'Leads merged successfully');
        if (onMergeComplete) onMergeComplete(data.lead);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to merge leads');
      }
    }
    setMerging(false);
  };

  const existingNotes = fullExisting?.notes?.length || 0;
  const existingDocs = fullExisting?.documents?.length || 0;
  const existingShared = fullExisting?.shared_with?.length || 0;
  const existingTags = fullExisting?.tags?.length || 0;
  const newTags = typeof newLeadData.tags === 'string'
    ? newLeadData.tags.split(',').filter(t => t.trim()).length
    : (newLeadData.tags?.length || 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="merge-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <GitMerge className="w-5 h-5 text-[#0047AB]" /> Merge Leads
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-slate-500 -mt-2 mb-3">
          Select which value to keep for each field. Notes, documents, shared members, and tags will be combined automatically.
        </p>

        {/* Field-by-field picker */}
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-[140px_1fr_40px_1fr] bg-slate-50 border-b border-slate-200 px-3 py-2">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-medium">Field</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#0047AB] font-medium">Existing Lead</div>
            <div />
            <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-600 font-medium">New Data</div>
          </div>

          {/* Rows */}
          {MERGE_FIELDS.map(f => {
            const existVal = getVal(f.key, 'existing');
            const newVal = getVal(f.key, 'new');
            const isExisting = selections[f.key] === 'existing';
            const bothEmpty = !existVal && !newVal;
            const sameVal = existVal === newVal;
            return (
              <div key={f.key} className="grid grid-cols-[140px_1fr_40px_1fr] items-center px-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50" data-testid={`merge-field-${f.key}`}>
                <div className="text-xs font-medium text-slate-600">{f.label}</div>
                <button
                  onClick={() => !bothEmpty && !sameVal && toggle(f.key)}
                  className={`text-left text-sm px-2 py-1 rounded-sm truncate transition-all ${
                    isExisting && !bothEmpty ? 'bg-blue-50 border border-[#0047AB]/30 text-slate-900 font-medium' : 'text-slate-400 hover:bg-slate-50'
                  } ${bothEmpty || sameVal ? 'cursor-default' : 'cursor-pointer'}`}
                  disabled={bothEmpty || sameVal}
                  data-testid={`pick-existing-${f.key}`}
                >
                  {f.key === 'status' && existVal ? <span className={`inline-block text-xs px-1.5 py-0.5 rounded-sm ${statusColor(existVal)}`}>{existVal}</span> : (existVal || <span className="text-slate-300">—</span>)}
                  {isExisting && existVal && !sameVal && <Check className="inline-block w-3 h-3 ml-1 text-[#0047AB]" />}
                </button>
                <div className="flex justify-center">
                  {!bothEmpty && !sameVal && <ArrowRight className="w-3.5 h-3.5 text-slate-300" />}
                </div>
                <button
                  onClick={() => !bothEmpty && !sameVal && toggle(f.key)}
                  className={`text-left text-sm px-2 py-1 rounded-sm truncate transition-all ${
                    !isExisting && !bothEmpty ? 'bg-emerald-50 border border-emerald-300 text-slate-900 font-medium' : 'text-slate-400 hover:bg-slate-50'
                  } ${bothEmpty || sameVal ? 'cursor-default' : 'cursor-pointer'}`}
                  disabled={bothEmpty || sameVal}
                  data-testid={`pick-new-${f.key}`}
                >
                  {f.key === 'status' && newVal ? <span className={`inline-block text-xs px-1.5 py-0.5 rounded-sm ${statusColor(newVal)}`}>{newVal}</span> : (newVal || <span className="text-slate-300">—</span>)}
                  {!isExisting && newVal && !sameVal && <Check className="inline-block w-3 h-3 ml-1 text-emerald-600" />}
                </button>
              </div>
            );
          })}
        </div>

        {/* Combined data preview */}
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mt-2">
          <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-medium mb-2">Will be combined automatically</p>
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
              <span>{existingNotes} note{existingNotes !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <FileText className="w-3.5 h-3.5 text-slate-400" />
              <span>{existingDocs} document{existingDocs !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <Share2 className="w-3.5 h-3.5 text-slate-400" />
              <span>{existingShared} shared member{existingShared !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <Tag className="w-3.5 h-3.5 text-slate-400" />
              <span>{existingTags + newTags} tag{existingTags + newTags !== 1 ? 's' : ''} (merged)</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="merge-cancel-btn">Cancel</Button>
          <Button onClick={handleMerge} disabled={merging} className="bg-[#0047AB] hover:bg-[#003580] text-white rounded-sm" data-testid="merge-confirm-btn">
            {merging ? (
              <><div className="animate-spin w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full mr-1.5" /> Merging...</>
            ) : (
              <><GitMerge className="w-3.5 h-3.5 mr-1.5" /> Merge Leads</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
